import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { TextField, Select, MenuItem, FormControl, InputLabel, Alert, Grid, Button, Typography } from '@mui/material';
import { useDataProvider } from 'react-admin';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { EmployeeSearchAutocomplete } from '@/components/employee/EmployeeSearchAutocomplete';
import type { EmployeeSearchResult } from '@/components/employee';
import { stepValidator } from '@/components/wizard/validateWithZod';
import { buildTransitionChain, useRuleEvaluation } from '@/components/workflow';

interface PrefilledField {
  field: string;
  label: string;
  sourceValue: unknown;
  currentValue: unknown;
  confirmed: boolean;
}

const STEPS = [
  { id: 'details', title: 'PIP Details' },
  { id: 'gaps', title: 'Performance Gaps' },
  { id: 'objectives', title: 'Measurable Objectives' },
  { id: 'support', title: 'Support Plan' },
  { id: 'issue', title: 'Issue PIP' },
];

const OUTCOME_TYPES = ['Performance Improved', 'Final Warning', 'Termination', 'Successful'];

interface Gap {
  description: string;
}

interface Objective {
  gapIndex: number;
  description: string;
  targetDate: string;
}

interface FormValues {
  caseNumber: string;
  employeeName: string;
  employeeId: string;
  openedDate: string;
  targetDate: string;
  gaps: Gap[];
  objectives: Objective[];
  trainingProvided: string;
  coachingAssigned: string;
  managerInvolved: string;
  issueDate: string;
}

const INITIAL_VALUES: FormValues = {
  caseNumber: '', employeeName: '', employeeId: '', openedDate: '', targetDate: '',
  gaps: [{ description: '' }],
  objectives: [],
  trainingProvided: '', coachingAssigned: '', managerInvolved: '',
  issueDate: '',
};

const stepSchemas = {
  details: z.object({
    employeeName: z.string().min(1, 'Employee name is required'),
    openedDate: z.string().min(1, 'Opened date is required'),
    targetDate: z.string().min(1, 'Target completion date is required'),
  }),
  support: z.object({
    trainingProvided: z.string().min(1, 'Training decision is required'),
    coachingAssigned: z.string().min(1, 'Coaching decision is required'),
    managerInvolved: z.string().min(1, 'Manager involvement is required'),
  }),
  issue: z.object({
    issueDate: z.string().min(1, 'Issue date is required'),
  }),
};

const validateStep = (stepId: string, values: FormValues) => {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) {
    if (stepId === 'gaps') {
      const validGaps = values.gaps.filter(g => g.description.trim().length >= 20);
      if (validGaps.length === 0) return { isValid: false, errors: { 'gaps.0': 'At least one performance gap (min 20 chars) is required' } };
      return { isValid: true, errors: {} };
    }
    if (stepId === 'objectives') {
      const validGaps = values.gaps.filter(g => g.description.trim().length >= 20);
      const validObjectives = values.objectives.filter(o => o.description.trim().length > 0);
      if (validGaps.length > 0 && validObjectives.length < validGaps.length) {
        return { isValid: false, errors: { objectives: 'Each gap must have at least one measurable objective' } };
      }
      return { isValid: true, errors: {} };
    }
    return { isValid: true, errors: {} };
  }
  const result = stepValidator(schema)(values as unknown as Record<string, string>);
  if (stepId === 'details' && values.openedDate && values.targetDate) {
    const target = new Date(values.targetDate);
    const opened = new Date(values.openedDate);
    const daysDiff = (target.getTime() - opened.getTime()) / (1000 * 60 * 60 * 24);
    if (daysDiff < 30) {
      result.errors.targetDate = 'Target date must be at least 30 days from opened date (Schedule 8)';
      result.isValid = false;
    }
  }
  return result;
};

export const PipWizard = () => {
  const { employeeId } = useParams();
  const dataProvider = useDataProvider();

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchResult | null>(null);
  const [prefilledFields, setPrefilledFields] = useState<PrefilledField[]>([]);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const context: Record<string, unknown> = {
      pipId: null,
      employeeId: v.employeeId,
      openedDate: v.openedDate,
      targetDate: v.targetDate,
      reviewDate: null,
      outcome: null,
      gaps: v.gaps.filter((g: Gap) => g.description.trim()),
      objectives: v.objectives,
      supportProvided: [
        ...(v.trainingProvided === 'Yes' || v.trainingProvided === 'Planned' ? [{ type: 'training', description: v.trainingProvided }] : []),
        ...(v.coachingAssigned === 'Yes' ? [{ type: 'coaching', description: v.coachingAssigned }] : []),
      ],
      reviewHistory: [],
      supportStartDate: v.issueDate || null,
    };

    const pipResult = await dataProvider.create('pips', { data: formValues });
    const pipId = pipResult.data.id;

    const wizardEvents = ['ISSUE_TO_EMPLOYEE'];
    const { finalState, transitions } = buildTransitionChain('pip', 'draft', wizardEvents);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'pip',
        entityId: pipId,
        entityName: `PIP: ${v.employeeName}`,
        currentState: { value: finalState, context },
        status: 'active',
        workflowVersion: '1.0',
        startedAt: new Date().toISOString(),
        lastActionAt: new Date().toISOString(),
        pausedAt: null,
        breakpointId: null,
        relatedEntityId: pipId,
        transitions,
      },
    });

    return { id: pipId };
  }, [dataProvider]);

  const {
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues: wizardUpdateValues,
    validateStep: wizardValidateStep,
    complete,
    reset,
  } = useWizard({
    steps: STEPS,
    initialValues: { ...INITIAL_VALUES, employeeId: employeeId || '' },
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate: evaluateRule, isEvaluating: isEvaluatingRule, result: ruleResult } = useRuleEvaluation({
    ruleId: 'pip-guards',
    context: {
      entityId: createdId || '',
      entityType: 'process',
      currentState: STEPS[currentStepIndex]?.id || 'initiated',
      event: 'SUBMIT',
      employeeId: (values as unknown as FormValues).employeeId,
      openedDate: (values as unknown as FormValues).openedDate,
      targetDate: (values as unknown as FormValues).targetDate,
    },
  });

  useEffect(() => {
    if (STEPS[currentStepIndex]?.id === 'issue') {
      evaluateRule();
    }
  }, [currentStepIndex, evaluateRule]);

  const vals = values as unknown as FormValues;

  const updateValues = (field: keyof FormValues, value: unknown) => {
    wizardUpdateValues({ [field]: value as unknown });
  };

  const handleEmployeeSelect = (employee: EmployeeSearchResult) => {
    setSelectedEmployee(employee);
    const fields: PrefilledField[] = [
      { field: 'department', label: 'Department', sourceValue: employee.organisationalHierarchy?.department || '-', currentValue: '', confirmed: false },
      { field: 'position', label: 'Position', sourceValue: employee.seniority?.jobTitle || '-', currentValue: '', confirmed: false },
    ];
    setPrefilledFields(fields);
    wizardUpdateValues({
      employeeName: employee.fullName,
      employeeId: employee.employeeNumber,
    });
  };

  const handleClearEmployee = () => {
    setSelectedEmployee(null);
    setPrefilledFields([]);
  };

  const handleNext = () => {
    const result = validateStep(STEPS[currentStepIndex].id, vals);
    if (!result.isValid) {
      wizardValidateStep(() => result);
      return;
    }
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const getStepDescription = (stepId: string): string => {
    switch (stepId) {
      case 'details': return 'Enter the PIP case details';
      case 'gaps': return 'Document performance gaps';
      case 'objectives': return 'Define measurable objectives';
      case 'support': return 'Outline support plan';
      case 'issue': return 'Issue the PIP to employee';
      default: return '';
    }
  };

  const renderFormFields = (stepId: string): React.ReactNode => {
    switch (stepId) {
      case 'details':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <EmployeeSearchAutocomplete
                label="Search Employee"
                onEmployeeSelect={handleEmployeeSelect}
                onClear={handleClearEmployee}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Employee Name" value={values.employeeName} onChange={e => updateValues('employeeName', e.target.value)} fullWidth error={!!errors.employeeName} helperText={errors.employeeName} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Employee #" value={values.employeeId} onChange={e => updateValues('employeeId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Opened Date" type="date" value={values.openedDate} onChange={e => updateValues('openedDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} error={!!errors.openedDate} helperText={errors.openedDate} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Target Completion Date" type="date" value={values.targetDate} onChange={e => updateValues('targetDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} error={!!errors.targetDate} helperText={errors.targetDate || 'Min 30 days per LRA Schedule 8'} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">PIP must meet Schedule 8 LRA requirements — measurable objectives and support plan required</Alert>
            </Grid>
          </Grid>
        );

      case 'gaps':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              {vals.gaps.map((gap: { description: string }, idx: number) => (
                <TextField
                  key={idx}
                  label={`Gap ${idx + 1}`}
                  value={gap.description}
                  onChange={e => {
                    const newGaps = [...vals.gaps];
                    newGaps[idx].description = e.target.value;
                    updateValues('gaps', newGaps);
                  }}
                  fullWidth
                  multiline
                  rows={2}
                  error={!!errors[`gaps.${idx}`]}
                  helperText={errors[`gaps.${idx}`] || 'Describe specific performance shortfall'}
                  sx={{ mb: 1 }}
                />
              ))}
              <Button size="small" onClick={() => updateValues('gaps', [...vals.gaps, { description: '' }])}>+ Add Another Gap</Button>
            </Grid>
          </Grid>
        );

      case 'objectives':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              {vals.gaps.map((gap: { description: string }, gapIdx: number) => (
                <Grid container key={gapIdx} spacing={1} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Grid size={{ xs: 12 }}>
                    {gap.description.substring(0, 50)}...
                  </Grid>
                  {vals.objectives.filter((o: { gapIndex: number }) => o.gapIndex === gapIdx).map((obj: { description: string; gapIndex: number; targetDate: string }, objIdx: number) => (
                    <Grid container spacing={1} key={objIdx}>
                      <Grid size={{ xs: 8 }}>
                        <TextField label={`Objective ${objIdx + 1}`} value={obj.description} onChange={e => {
                          const newObjs = [...vals.objectives];
                          const existing = newObjs.findIndex(o => o.gapIndex === gapIdx && o.targetDate === obj.targetDate);
                          if (existing >= 0) newObjs[existing].description = e.target.value;
                          else newObjs.push({ gapIndex: gapIdx, description: e.target.value, targetDate: '' });
                          updateValues('objectives', newObjs);
                        }} fullWidth size="small" />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <TextField label="Target Date" type="date" value={obj.targetDate} onChange={e => {
                          const newObjs = [...vals.objectives];
                          const existing = newObjs.findIndex(o => o.gapIndex === gapIdx && o.description === obj.description);
                          if (existing >= 0) newObjs[existing].targetDate = e.target.value;
                          updateValues('objectives', newObjs);
                        }} fullWidth size="small" InputLabelProps={{ shrink: true }} />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              ))}
            </Grid>
          </Grid>
        );

      case 'support':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth error={!!errors.trainingProvided}>
                <InputLabel>Training to be Provided</InputLabel>
                <Select value={values.trainingProvided} label="Training to be Provided" onChange={e => updateValues('trainingProvided', e.target.value)}>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Planned">Planned</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth error={!!errors.coachingAssigned}>
                <InputLabel>Coaching Assigned</InputLabel>
                <Select value={values.coachingAssigned} label="Coaching Assigned" onChange={e => updateValues('coachingAssigned', e.target.value)}>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth error={!!errors.managerInvolved}>
                <InputLabel>Manager Involved</InputLabel>
                <Select value={values.managerInvolved} label="Manager Involved" onChange={e => updateValues('managerInvolved', e.target.value)}>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">All support actions must be documented per LRA Schedule 8 for fair dismissal requirements</Alert>
            </Grid>
          </Grid>
        );

      case 'issue':
        return (
          <Grid container spacing={2}>
            {ruleResult && !ruleResult.allowed && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Guard Check</Typography>
                  <Typography variant="body2">{ruleResult.reason}</Typography>
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Issue Date" type="date" value={values.issueDate} onChange={e => updateValues('issueDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} error={!!errors.issueDate} helperText={errors.issueDate} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="success">PIP will be active — first review scheduled in 30 days</Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onNext={handleNext}
      onComplete={handleNext}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Performance Improvement Plan Initiated"
          processId={createdId || undefined}
          fields={[
            { label: 'Employee', value: vals.employeeName },
            { label: 'Opened', value: vals.openedDate },
            { label: 'Target', value: vals.targetDate },
            { label: 'Gaps', value: `${vals.gaps.filter((g: { description: string }) => g.description).length} documented` },
            { label: 'Training', value: vals.trainingProvided },
          ]}
          footerActions={[
            { label: 'View PIP', to: '/pips' },
            { label: 'Start Another', onClick: () => {
              reset();
            }},
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={STEPS[currentStepIndex].title}
          description={getStepDescription(STEPS[currentStepIndex].id)}
          feedback={stepFeedback ? { type: stepFeedback.type, messages: stepFeedback.messages } : undefined}
        >
          {renderFormFields(STEPS[currentStepIndex].id)}
        </WizardStep>
      )}
    </WizardLayout>
  );
};